import React, { useEffect, useMemo, useState } from "react";
import { Calculator, Eye, Flag, History, Percent, Wallet } from "lucide-react";
import AdminPageShell from "../components/AdminPageShell";
import API from "../../../util/API";
import { getCountryName } from "../../../util/countryNames";
import { formatMoney as fmtMoney } from "../../../util/money";

const cx = (...arr) => arr.filter(Boolean).join(" ");

const toFlagClass = (code) => {
  const raw = String(code || "").trim().toLowerCase();
  if (raw.length !== 2) return "";
  return `fi fi-${raw}`;
};

const Card = ({ children, className }) => (
  <div className={cx("bg-white border border-slate-200 rounded-2xl", className)}>{children}</div>
);

const FieldBase =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30";

const SelectBase =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30";

const PrimaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors",
      className
    )}
  />
);

const SecondaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors",
      className
    )}
  />
);

const Chip = ({ children, tone = "slate" }) => {
  const toneMap = {
    slate: "border-slate-200 bg-slate-50 text-slate-800",
    white: "border-slate-200 bg-white text-slate-700",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
  };

  return (
    <span
      className={cx(
        "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-black",
        toneMap[tone] || toneMap.slate
      )}
    >
      {children}
    </span>
  );
};

const PricingAndProfitManagement = () => {
  const [markupType, setMarkupType] = useState("percentage");
  const [globalMarkup, setGlobalMarkup] = useState(10);
  const [currencyConversion, setCurrencyConversion] = useState("auto");
  const [showCompareAtPrice, setShowCompareAtPrice] = useState(false);
  const [b2bMode, setB2bMode] = useState(false);

  const [markupDraftByCountryCode, setMarkupDraftByCountryCode] = useState({});
  const [markupLoading, setMarkupLoading] = useState(false);
  const [markupSaving, setMarkupSaving] = useState(false);
  const [markupError, setMarkupError] = useState(null);
  const [markupNotice, setMarkupNotice] = useState(null);
  const [showMarkupModal, setShowMarkupModal] = useState(false);

  const [priceLogs, setPriceLogs] = useState([]);
  const [newLogMessage, setNewLogMessage] = useState("");

  const [costPrice, setCostPrice] = useState(0);
  const [finalPrice, setFinalPrice] = useState(0);
  const [margin, setMargin] = useState(0);

  const [enabledCountryMeta, setEnabledCountryMeta] = useState([]);

  const [taxTableViewed, setTaxTableViewed] = useState(false);
  const [taxDraftByCountryCode, setTaxDraftByCountryCode] = useState({});
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxSaving, setTaxSaving] = useState(false);
  const [taxError, setTaxError] = useState(null);
  const [taxNotice, setTaxNotice] = useState(null);

  const estimatedProfit = useMemo(() => {
    const cost = Number(costPrice);
    if (!Number.isFinite(cost) || cost <= 0) return null;
    return (Number(margin) / cost) * 100;
  }, [costPrice, margin]);

  const MARKUP_PREVIEW_BASE = 100;

  const supportedCountries = useMemo(() => {
    const list = Array.isArray(enabledCountryMeta) ? enabledCountryMeta : [];
    const normalized = list
      .map((c) => String(c?.countryCode || c || '').trim().toUpperCase())
      .filter(Boolean);
    return Array.from(new Set(normalized)).sort((a, b) => a.localeCompare(b));
  }, [enabledCountryMeta]);

  const enabledCountryNameByCode = useMemo(() => {
    const map = {};
    for (const entry of enabledCountryMeta || []) {
      const code = String(entry?.countryCode || entry || '').trim().toUpperCase();
      const name = String(entry?.countryName || '').trim();
      if (code && name) map[code] = name;
    }
    return map;
  }, [enabledCountryMeta]);
  const fetchSelectorConfig = async () => {
    try {
      const res = await API.get("/settings/countries");
      const list = Array.isArray(res.data?.countries) ? res.data.countries : [];
      const cleaned = list
        .map((c) => {
          if (typeof c === "string") return { countryCode: c };
          return {
            countryCode: c?.countryCode,
            countryName: c?.countryName,
          };
        })
        .filter((c) => c?.countryCode);
      setEnabledCountryMeta(cleaned);
    } catch (e) {
      setEnabledCountryMeta([]);
    }
  };

  const viewTaxTable = async () => {
    setTaxError(null);
    setTaxNotice(null);
    setTaxLoading(true);
    try {
      const res = await API.get('/settings/tax-table');
      const rows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
      const byCode = {};
      for (const r of rows) {
        const cc = String(r?.countryCode || '').trim().toUpperCase();
        const n = Number(r?.tax_percent);
        if (!cc) continue;
        byCode[cc] = Number.isFinite(n) ? n : 0;
      }

      const draft = {};
      for (const cc of supportedCountries) {
        draft[cc] = String(byCode[cc] ?? 0);
      }
      setTaxDraftByCountryCode(draft);
      setTaxTableViewed(true);
    } catch (e) {
      setTaxError(e?.response?.data?.message || e?.message || 'Failed to load tax table');
      setTaxTableViewed(false);
      setTaxDraftByCountryCode({});
    } finally {
      setTaxLoading(false);
    }
  };

  const saveTaxTable = async () => {
    if (!taxTableViewed) {
      await viewTaxTable();
      if (!taxTableViewed) return;
    }

    setTaxError(null);
    setTaxNotice(null);
    setTaxSaving(true);

    try {
      const rows = supportedCountries.map((cc) => {
        const raw = taxDraftByCountryCode?.[cc];
        const n = raw === '' || raw === null || raw === undefined ? 0 : Number(raw);
        if (!Number.isFinite(n)) throw new Error(`Invalid tax (%) for ${cc}`);
        if (n < 0 || n > 100) throw new Error(`Tax (%) must be 0-100 for ${cc}`);
        return { countryCode: cc, tax_percent: n };
      });

      const res = await API.put('/settings/tax-table', { rows });
      const saved = Array.isArray(res?.data?.rows) ? res.data.rows : [];
      const byCode = {};
      for (const r of saved) {
        const cc = String(r?.countryCode || '').trim().toUpperCase();
        const n = Number(r?.tax_percent);
        if (!cc) continue;
        byCode[cc] = Number.isFinite(n) ? n : 0;
      }
      const nextDraft = { ...taxDraftByCountryCode };
      for (const cc of supportedCountries) {
        if (byCode[cc] !== undefined) nextDraft[cc] = String(byCode[cc]);
      }
      setTaxDraftByCountryCode(nextDraft);
      setTaxNotice('Saved');
    } catch (e) {
      setTaxError(e?.response?.data?.message || e?.message || 'Failed to save tax table');
    } finally {
      setTaxSaving(false);
    }
  };

  useEffect(() => {
    fetchSelectorConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const viewMarkupTable = async () => {
    setMarkupError(null);
    setMarkupNotice(null);
    setMarkupLoading(true);
    try {
      const res = await API.get('/settings/markup-table');
      const rows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
      const byCode = {};
      for (const r of rows) {
        const cc = String(r?.countryCode || '').trim().toUpperCase();
        const n = Number(r?.percent);
        if (!cc) continue;
        byCode[cc] = Number.isFinite(n) ? n : 0;
      }

      const draft = {};
      for (const cc of supportedCountries) {
        draft[cc] = String(byCode[cc] ?? 0);
      }
      setMarkupDraftByCountryCode(draft);
      setShowMarkupModal(true);
    } catch (e) {
      setMarkupError(e?.response?.data?.message || e?.message || 'Failed to load markup table');
    } finally {
      setMarkupLoading(false);
    }
  };

  const saveMarkupTable = async () => {
    setMarkupError(null);
    setMarkupNotice(null);
    setMarkupSaving(true);
    try {
      const rows = supportedCountries.map((cc) => {
        const raw = markupDraftByCountryCode?.[cc];
        const n = raw === '' || raw === null || raw === undefined ? 0 : Number(raw);
        if (!Number.isFinite(n)) throw new Error(`Invalid percent for ${cc}`);
        if (n < 0 || n > 100) throw new Error(`Percent must be 0-100 for ${cc}`);
        return { countryCode: cc, percent: n };
      });

      const res = await API.put('/settings/markup-table', { rows });
      const saved = Array.isArray(res?.data?.rows) ? res.data.rows : [];
      const byCode = {};
      for (const r of saved) {
        const cc = String(r?.countryCode || '').trim().toUpperCase();
        const n = Number(r?.percent);
        if (!cc) continue;
        byCode[cc] = Number.isFinite(n) ? n : 0;
      }
      const nextDraft = { ...markupDraftByCountryCode };
      for (const cc of supportedCountries) {
        if (byCode[cc] !== undefined) nextDraft[cc] = String(byCode[cc]);
      }
      setMarkupDraftByCountryCode(nextDraft);
      setMarkupNotice('Saved');
    } catch (e) {
      setMarkupError(e?.response?.data?.message || e?.message || 'Failed to save markup table');
    } finally {
      setMarkupSaving(false);
    }
  };

  const handlePriceLogChange = (newLog) => {
    setPriceLogs((prev) => [newLog, ...prev]);
  };

  const handleAddLog = () => {
    const message = newLogMessage.trim();
    if (!message) return;
    handlePriceLogChange({
      id: Date.now(),
      message,
      timestamp: new Date().toLocaleString(),
    });
    setNewLogMessage("");
  };

  const handlePricePreview = () => {
    const cost = Number(costPrice);
    const markup = Number(globalMarkup);
    if (!Number.isFinite(cost) || cost < 0) return;
    if (!Number.isFinite(markup) || markup < 0) return;

    const calculatedPrice =
      markupType === "fixed" ? cost + markup : cost + cost * (markup / 100);
    const calculatedMargin = calculatedPrice - cost;
    setFinalPrice(calculatedPrice);
    setMargin(calculatedMargin);
  };

  return (
    <AdminPageShell
      title="Pricing & Profit Management"
      description="Configure markup rules, country pricing, and preview margins. (Mock settings)"
      chips={
        <>
          <Chip>
            <Percent className="h-4 w-4" />
            {markupType === "fixed" ? "Fixed" : "%"} markup
          </Chip>
          <Chip tone="white">
            Global: {Number(globalMarkup) || 0}
            {markupType === "fixed" ? "" : "%"}
          </Chip>
          <Chip tone={b2bMode ? "rose" : "slate"}>{b2bMode ? "B2B Mode" : "B2C Mode"}</Chip>
        </>
      }
      actions={
        <>
          <SecondaryButton type="button" onClick={() => {
            setFinalPrice(0);
            setMargin(0);
          }}>
            Reset
          </SecondaryButton>
          <PrimaryButton type="button" onClick={handlePricePreview}>
            <Calculator className="h-4 w-4" />
            Preview
          </PrimaryButton>
        </>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Global Markup Rules */}
        <Card className="p-5">
          <div className="flex items-center gap-2">
            <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
            <Wallet className="h-4 w-4 text-slate-700" />
            <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Global Markup Rules</h2>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                Markup Type
              </label>
              <select value={markupType} onChange={(e) => setMarkupType(e.target.value)} className={SelectBase}>
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                Global Markup
              </label>
              <input
                type="number"
                value={globalMarkup}
                onChange={(e) => setGlobalMarkup(e.target.value)}
                min="0"
                step="0.01"
                className={FieldBase}
              />
              <p className="mt-2 text-xs text-slate-500">
                {markupType === "fixed" ? "Applied as a fixed amount." : "Applied as a percentage of cost."}
              </p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                Cost Price
              </label>
              <input
                type="number"
                value={costPrice}
                onChange={(e) => setCostPrice(e.target.value)}
                min="0"
                step="0.01"
                className={FieldBase}
                placeholder="0.00"
              />
            </div>

            <div className="md:col-span-2 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm">
                <p className="text-slate-900 font-semibold">Final Price</p>
                <p className="text-slate-600">{fmtMoney(Number(finalPrice) || 0, { from: "USD", to: "USD" })}</p>
              </div>
              <div className="text-sm">
                <p className="text-slate-900 font-semibold">Margin</p>
                <p className="text-slate-600">{fmtMoney(Number(margin) || 0, { from: "USD", to: "USD" })}</p>
              </div>
              <div className="text-sm">
                <p className="text-slate-900 font-semibold">Estimated Profit</p>
                <p className="text-slate-600">{estimatedProfit == null ? "—" : `${estimatedProfit.toFixed(2)}%`}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Country-wise Pricing */}
        <Card className="p-5">
          <div className="flex items-center gap-2">
            <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
            <Flag className="h-4 w-4 text-slate-700" />
            <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Country-wise Pricing</h2>
          </div>

          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-slate-900">Country Markup (Profit %)</p>
                <p className="text-xs text-slate-600">Applied to both offer_sale_price and sku_price when no SKU override exists.</p>
              </div>
              <SecondaryButton type="button" onClick={viewMarkupTable} disabled={markupLoading || markupSaving}>
                Edit
              </SecondaryButton>
            </div>
            {markupError ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
                {markupError}
              </div>
            ) : null}
            {markupNotice ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                {markupNotice}
              </div>
            ) : null}
          </div>
        </Card>

        {/* Compare-at Price + Mode */}
        <Card className="p-5">
          <div className="flex items-center gap-2">
            <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
            <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Display & Mode</h2>
          </div>

          <div className="mt-4 space-y-4">
            <label className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4">
              <div>
                <p className="font-semibold text-slate-900">Compare-at Price</p>
                <p className="text-sm text-slate-600">Show strike-through “compare at” pricing on storefront</p>
              </div>
              <input
                type="checkbox"
                checked={showCompareAtPrice}
                onChange={(e) => setShowCompareAtPrice(e.target.checked)}
                className="h-5 w-5 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]/30"
              />
            </label>

            <label className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4">
              <div>
                <p className="font-semibold text-slate-900">B2B Mode</p>
                <p className="text-sm text-slate-600">Enable business pricing behavior (mock toggle)</p>
              </div>
              <input
                type="checkbox"
                checked={b2bMode}
                onChange={(e) => setB2bMode(e.target.checked)}
                className="h-5 w-5 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]/30"
              />
            </label>
          </div>
        </Card>

        {/* Manual Override Logs */}
        <Card className="p-5">
          <div className="flex items-center gap-2">
            <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
            <History className="h-4 w-4 text-slate-700" />
            <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Manual Override Logs</h2>
          </div>

          <div className="mt-4 space-y-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={newLogMessage}
                onChange={(e) => setNewLogMessage(e.target.value)}
                placeholder="Add a note about a manual price override"
                className={FieldBase}
              />
              <PrimaryButton type="button" onClick={handleAddLog} className="whitespace-nowrap">
                Add
              </PrimaryButton>
            </div>

            {priceLogs.length > 0 ? (
              <div className="space-y-2">
                {priceLogs.map((log) => (
                  <div key={log.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-sm font-semibold text-slate-900">{log.message}</p>
                    <p className="text-xs text-slate-600 mt-1">{log.timestamp}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                No manual overrides made yet.
              </div>
            )}
          </div>
        </Card>

        {/* Tax Management */}
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center gap-2">
            <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
            <Percent className="h-4 w-4 text-slate-700" />
            <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">Tax Management</h2>
          </div>

          <p className="mt-2 text-sm text-slate-600">
            Set tax percentage per country (applies to all products). Countries come from the Storefront Currency Selector list.
          </p>

          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <SecondaryButton type="button" onClick={viewTaxTable} disabled={taxLoading || taxSaving}>
              <Eye className="h-4 w-4" />
              {taxLoading ? 'Loading…' : 'View'}
            </SecondaryButton>
            <PrimaryButton type="button" onClick={saveTaxTable} disabled={taxLoading || taxSaving || !taxTableViewed}>
              {taxSaving ? 'Saving…' : 'Save'}
            </PrimaryButton>
            {taxNotice ? <span className="text-sm font-semibold text-slate-700">{taxNotice}</span> : null}
            {taxError ? <span className="text-sm font-semibold text-rose-700">{taxError}</span> : null}
          </div>

          {!taxTableViewed ? (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              Click <span className="font-semibold text-slate-900">View</span> to load and edit country tax percentages.
            </div>
          ) : (
            <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
              <div className="max-h-[420px] overflow-auto">
                <table className="w-full text-left">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">Country</th>
                      <th className="px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">Tax (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {supportedCountries.map((cc) => (
                      <tr key={cc} className="border-t border-slate-200">
                        <td className="px-4 py-3 text-sm text-slate-900">
                          <span className="font-black">{cc}</span>
                          <span className="text-slate-500"> — {getCountryName(cc)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            className={FieldBase}
                            value={taxDraftByCountryCode?.[cc] ?? '0'}
                            onChange={(e) =>
                              setTaxDraftByCountryCode((prev) => ({
                                ...(prev || {}),
                                [cc]: e.target.value,
                              }))
                            }
                            placeholder="0"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>
      </div>


      {showMarkupModal ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/40"
            onClick={() => setShowMarkupModal(false)}
            role="button"
            tabIndex={-1}
            aria-label="Close"
          />

          <div className="relative w-full max-w-3xl rounded-2xl bg-white border border-slate-200 shadow-xl">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div className="min-w-0">
                <p className="text-sm font-black tracking-tight text-slate-900">Country Markup</p>
                <p className="text-xs text-slate-600">Set profit percent per country</p>
                <p className="mt-1 text-xs font-semibold text-slate-700">
                  Total countries: {supportedCountries.length}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <SecondaryButton type="button" onClick={() => setShowMarkupModal(false)} className="px-4">
                  Close
                </SecondaryButton>
                <PrimaryButton type="button" onClick={saveMarkupTable} disabled={markupSaving}>
                  {markupSaving ? 'Saving…' : 'Save'}
                </PrimaryButton>
              </div>
            </div>

            <div className="max-h-[70vh] overflow-auto">
              <div className="px-5 py-4">
                <p className="mb-4 text-xs text-slate-500">
                  Preview shows $100 × (1 + profit%).
                </p>
                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <table className="w-full text-left">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">Country</th>
                        <th className="px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">Percent</th>
                        <th className="px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">Preview ($100)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supportedCountries.map((cc) => (
                        <tr key={cc} className="border-t border-slate-200">
                          <td className="px-4 py-3 text-sm text-slate-900">
                            <span className="inline-flex items-center gap-2">
                              {toFlagClass(cc) ? (
                                <span
                                  className={cx("inline-block h-4 w-6 rounded-sm", toFlagClass(cc))}
                                  aria-hidden="true"
                                />
                              ) : (
                                <span className="inline-block h-4 w-6 rounded-sm bg-slate-200" aria-hidden="true" />
                              )}
                              <span className="font-semibold">{enabledCountryNameByCode[cc] || getCountryName(cc)}</span>
                              <span className="text-xs text-slate-500">{cc}</span>
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              className={FieldBase}
                              value={markupDraftByCountryCode?.[cc] ?? '0'}
                              onChange={(e) =>
                                setMarkupDraftByCountryCode((prev) => ({
                                  ...(prev || {}),
                                  [cc]: e.target.value,
                                }))
                              }
                              placeholder="0"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700">
                            {fmtMoney(
                              MARKUP_PREVIEW_BASE +
                                MARKUP_PREVIEW_BASE *
                                  (Number(markupDraftByCountryCode?.[cc] || 0) / 100),
                              { from: "USD", to: "USD" }
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

    </AdminPageShell>
  );
};

export default PricingAndProfitManagement;
